import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  getReportFiles, 
  deleteReportFile, 
  downloadReconciliationReportFile,
  ReportFile 
} from 'services/reconciliationService';
import { IoTrashOutline, IoDownloadOutline, IoRefreshOutline } from 'react-icons/io5';

interface FileManagementProps {
  onRefresh?: () => void;
}

const FileManagement: React.FC<FileManagementProps> = ({ onRefresh }) => {
  const [files, setFiles] = useState<ReportFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getReportFiles();
      setFiles(response.files);
    } catch (error) {
      console.error('Failed to load files:', error);
      setError('Failed to load report files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const handleDelete = async (jobId: number, fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      return;
    }

    try {
      setDeleting(jobId);
      await deleteReportFile(jobId);
      await loadFiles(); // Refresh the list
      onRefresh?.(); // Notify parent component
    } catch (error) {
      console.error('Failed to delete file:', error);
      setError('Failed to delete file');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownload = async (file: ReportFile) => {
    try {
      await downloadReconciliationReportFile(file.jobId, file.fileName);
    } catch (error) {
      console.error('Failed to download file:', error);
      setError('Failed to download file');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>Report Files</Title>
          <RefreshButton onClick={loadFiles} disabled={loading}>
            <IoRefreshOutline size={16} />
          </RefreshButton>
        </Header>
        <LoadingMessage>Loading files...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Report Files ({files.length})</Title>
        <RefreshButton onClick={loadFiles} disabled={loading}>
          <IoRefreshOutline size={16} />
        </RefreshButton>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {files.length === 0 ? (
        <EmptyMessage>No report files found</EmptyMessage>
      ) : (
        <FileList>
          {files.map((file) => (
            <FileItem key={file.jobId}>
              <FileInfo>
                <FileName>{file.fileName}</FileName>
                <FileDetails>
                  <FileDetail>
                    <strong>Format:</strong> {file.format.toUpperCase()}
                  </FileDetail>
                  <FileDetail>
                    <strong>Size:</strong> {formatFileSize(file.size)}
                  </FileDetail>
                  <FileDetail>
                    <strong>Created:</strong> {formatDate(file.createdAt)}
                  </FileDetail>
                </FileDetails>
              </FileInfo>
              <FileActions>
                <ActionButton
                  onClick={() => handleDownload(file)}
                  title="Download"
                  primary
                >
                  <IoDownloadOutline size={16} />
                </ActionButton>
                <ActionButton
                  onClick={() => handleDelete(file.jobId, file.fileName)}
                  disabled={deleting === file.jobId}
                  title="Delete"
                  danger
                >
                  {deleting === file.jobId ? (
                    <Spinner />
                  ) : (
                    <IoTrashOutline size={16} />
                  )}
                </ActionButton>
              </FileActions>
            </FileItem>
          ))}
        </FileList>
      )}
    </Container>
  );
};

const Container = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  margin: 0;
  color: #333;
  font-size: 18px;
`;

const RefreshButton = styled.button`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #6c757d;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e9ecef;
    color: #495057;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  padding: 20px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #6c757d;
  padding: 40px 20px;
  font-style: italic;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  border: 1px solid #f5c6cb;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FileItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #f8f9fa;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
`;

const FileInfo = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
`;

const FileDetails = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const FileDetail = styled.div`
  font-size: 12px;
  color: #6c757d;

  strong {
    color: #495057;
  }
`;

const FileActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ primary?: boolean; danger?: boolean }>`
  background: ${props => 
    props.primary ? '#007bff' : 
    props.danger ? '#dc3545' : 
    '#6c757d'
  };
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
  min-width: 36px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => 
      props.primary ? '#0056b3' : 
      props.danger ? '#c82333' : 
      '#545b62'
    };
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Spinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default FileManagement;
